// Where the city's buildings, streets, trees and props go. Pure data (no
// rendering), so the same layout drives collision, tests and the renderer, and
// every player sees the same city. Variation comes from a fixed seed.
import { CITY, locations, seeded, type Point, type BoxObstacle, type Obstacle } from "./world-data.ts";
import { parkCourse } from "./park-lap.ts";

export type Kind = "building" | "street" | "prop" | "tree" | "plant" | "car";
export interface Placement {
  /** "<folder>/<model>" under public/assets, e.g. "city/Building_Small_1" */
  model: string;
  x: number;
  z: number;
  rot: number;
  scale: number;
  kind: Kind;
  /** collision circle radius in metres (0 = walk through) */
  collide: number;
  /** axis-aligned collision box (buildings) */
  box?: BoxObstacle;
  /** height in metres, for camera collision boxes */
  height?: number;
}

// Local bounds of the ready-made buildings (front faces +z; stoops stick out).
export const BUILDINGS = {
  "city/Building_Small_1": { minX: -7.23, maxX: 5.23, minZ: -12.23, maxZ: 2.31, h: 17 },
  "city/Building_Medium_2_001": { minX: -7.53, maxX: 7.53, minZ: -12.49, maxZ: 0.57, h: 25 },
  "city/Building_Large_2": { minX: -9.32, maxX: 11.32, minZ: -16.32, maxZ: 0.32, h: 28 },
} as const;
type BuildingId = keyof typeof BUILDINGS;
const BUILDING_IDS = Object.keys(BUILDINGS) as BuildingId[];

export const PLAZA_BLOCK = { x: 0, z: 0 };
export const PARK_BLOCK = { x: locations.park.x, z: locations.park.z };
export const BLOCK_INNER = CITY.L / 2 - CITY.KERB; // half-size of a block inside its pavements

export function buildLayout() {
  const rand = seeded(20261023);
  const r = (a = 0, b = 1) => a + rand() * (b - a);
  const pick = <V>(list: readonly V[]) => list[Math.floor(rand() * list.length)];
  const out: Placement[] = [];
  const add = (p: Placement) => (out.push(p), p);

  // 1. Streets: a crossroads at every grid point, 6 m road slices between.
  const { L, HALF, SLICE, SLICES, lines } = CITY;
  for (const x of lines)
    for (const z of lines) {
      const edge = Math.abs(x) > L || Math.abs(z) > L;
      const corner = Math.abs(x) > L && Math.abs(z) > L;
      // T-junctions along the outer edge face into the city.
      if (edge && !corner) {
        const rot = Math.abs(z) > L ? (z > 0 ? 0 : Math.PI) : x > 0 ? Math.PI / 2 : -Math.PI / 2;
        add({ model: "city/Street_TIntersection", x, z, rot: rot + Math.PI, scale: 1, kind: "street", collide: 0 });
      } else add({ model: "city/Street_4WayIntersection", x, z, rot: 0, scale: 1, kind: "street", collide: 0 });
    }
  for (let i = 0; i < lines.length - 1; i++)
    for (const s of lines)
      for (let k = 0; k < SLICES; k++) {
        const t = lines[i] + HALF + SLICE / 2 + k * SLICE;
        add({ model: "city/Street_4Lane", x: t, z: s, rot: 0, scale: 1, kind: "street", collide: 0 }); // east-west
        add({ model: "city/Street_4Lane", x: s, z: t, rot: Math.PI / 2, scale: 1, kind: "street", collide: 0 }); // north-south
      }

  // 2. Buildings. Each row runs along one side of a block with fronts on the
  // pavement; rows go round each block like a pinwheel so corners never overlap.
  function building(id: BuildingId, ax: number, az: number, rot: number) {
    const b = BUILDINGS[id];
    const c = Math.cos(rot), s = Math.sin(rot);
    // World-space axis-aligned footprint (rotations are multiples of 90 degrees).
    const corners = [[b.minX, b.minZ], [b.maxX, b.maxZ]].map(([lx, lz]) => [ax + lx * c + lz * s, az - lx * s + lz * c]);
    const [x0, x1] = [Math.min(corners[0][0], corners[1][0]), Math.max(corners[0][0], corners[1][0])];
    const [z0, z1] = [Math.min(corners[0][1], corners[1][1]), Math.max(corners[0][1], corners[1][1])];
    add({
      model: id, x: ax, z: az, rot, scale: 1, kind: "building", collide: 0, height: b.h,
      box: { x: (x0 + x1) / 2, z: (z0 + z1) / 2, hw: (x1 - x0) / 2, hd: (z1 - z0) / 2 },
    });
  }
  // A row of buildings: `origin` is the start of the building line, `t` the
  // direction along it, `rot` makes local +z face the street.
  function row(origin: Point, t: Point, rot: number, length: number) {
    let a = 0;
    while (true) {
      const id = pick(BUILDING_IDS), b = BUILDINGS[id];
      const width = b.maxX - b.minX;
      if (a + width > length) break;
      const along = a - b.minX; // flush with the start of the free space
      const setback = Math.max(0, b.maxZ - 0.3); // keep stoops off the walking line
      const nx = Math.sin(rot), nz = Math.cos(rot); // outward (street) normal
      building(id, origin.x + t.x * along - nx * setback, origin.z + t.z * along - nz * setback, rot);
      a += width + r(0.2, 1.2);
    }
  }
  const CORNER = 17; // leave room for the next row's corner building
  const inner = BLOCK_INNER;
  for (const bx of [-L, 0, L])
    for (const bz of [-L, 0, L]) {
      if ((bx === PLAZA_BLOCK.x && bz === PLAZA_BLOCK.z) || (bx === PARK_BLOCK.x && bz === PARK_BLOCK.z)) continue;
      row({ x: bx - inner, z: bz + inner }, { x: 1, z: 0 }, 0, 2 * inner - CORNER); // south side
      row({ x: bx + inner, z: bz + inner }, { x: 0, z: -1 }, Math.PI / 2, 2 * inner - CORNER); // east side
      row({ x: bx + inner, z: bz - inner }, { x: -1, z: 0 }, Math.PI, 2 * inner - CORNER); // north side
      row({ x: bx - inner, z: bz - inner }, { x: 0, z: 1 }, -Math.PI / 2, 2 * inner - CORNER); // west side
    }
  // Outer ring: buildings beyond the outer streets face inwards and close the city.
  // Rows stop short of the four corner crossroads, whose pavements reach further out.
  const outer = 1.5 * L + CITY.KERB, span = 1.5 * L - HALF;
  row({ x: span, z: outer }, { x: -1, z: 0 }, Math.PI, 2 * span); // beyond south street, facing north
  row({ x: -span, z: -outer }, { x: 1, z: 0 }, 0, 2 * span); // beyond north street, facing south
  row({ x: outer, z: -span }, { x: 0, z: 1 }, -Math.PI / 2, 2 * span); // beyond east street, facing west
  row({ x: -outer, z: span }, { x: 0, z: -1 }, Math.PI / 2, 2 * span); // beyond west street, facing east

  // 3. Along the kerbs: trees in planters alternating with street lights
  // (KayKit City Builder Bits), clear of crossroads.
  const kerb = CITY.ROAD + 0.9;
  for (const s of lines)
    for (let i = 0; i < lines.length - 1; i++)
      for (let t = lines[i] + HALF + 5, slot = 0; t < lines[i + 1] - HALF - 4; t += r(11, 15), slot++)
        for (const side of [-1, 1]) {
          // Outer streets only get trees and lights on the city side.
          if (Math.abs(s) > L && Math.sign(s) === side) continue;
          const light = slot % 2 === 1;
          if (!light && rand() < 0.15) continue;
          // [x, z, rotation of a light's arm over the road] for the east-west and north-south street
          for (const [x, z, armRot] of [[t, s + side * kerb, -side * Math.PI / 2], [s + side * kerb, t, side > 0 ? 0 : Math.PI]] as [number, number, number][]) {
            if (light) add({ model: "kaykit-city/streetlight.gltf", x, z, rot: armRot, scale: 6, kind: "prop", collide: 0.25 });
            else {
              add({ model: "city/Prop_Planter_Single", x, z, rot: 0, scale: 0.7, kind: "prop", collide: 0.75 });
              add({ model: `nature/CommonTree_${1 + Math.floor(rand() * 5)}`, x, z, rot: r(0, 6.3), scale: r(0.55, 0.7), kind: "tree", collide: 0 });
            }
          }
        }
  // Fire hydrants just past each crossroads; traffic lights on the corners of
  // the inner crossroads.
  for (const x of lines)
    for (const z of lines) {
      const [dx, dz] = pick([[1, 1], [1, -1], [-1, 1], [-1, -1]]);
      add({ model: "kaykit-city/firehydrant.gltf", x: x + dx * (HALF + 1.5), z: z + dz * (CITY.ROAD + 0.6), rot: r(0, 6.3), scale: 4, kind: "prop", collide: 0.3 });
      if (Math.abs(x) < L && Math.abs(z) < L)
        for (const [cx, cz] of [[1, 1], [1, -1], [-1, 1], [-1, -1]])
          add({ model: "kaykit-city/trafficlight_A.gltf", x: x + cx * 9.6, z: z + cz * 9.6, rot: Math.atan2(-cx, -cz), scale: 5, kind: "prop", collide: 0.3 });
    }
  // Parked cars in the kerbside lane, facing the way traffic flows on that side
  // (UK: drive on the left). Their model depends on the chosen vehicle style.
  const LANE = 4.5;
  for (const s of lines)
    for (let i = 0; i < lines.length - 1; i++)
      for (let t = lines[i] + HALF + 4, k = 0; t < lines[i + 1] - HALF - 4; t += 6.5, k++)
        for (const side of [-1, 1]) {
          if (rand() > 0.42) continue;
          // East-west street: +z lane drives towards -x, -z lane towards +x.
          addCar(t, s + side * LANE, side > 0 ? -Math.PI / 2 : Math.PI / 2);
          // North-south street: +x lane drives towards +z, -x lane towards -z.
          addCar(s + side * LANE, t, side > 0 ? 0 : Math.PI);
        }
  function addCar(x: number, z: number, rot: number) {
    const along = Math.abs(Math.sin(rot)) > 0.5; // car points along x
    add({
      model: "car", x, z, rot, scale: 1, kind: "car", collide: 0,
      box: { x, z, hw: along ? 2.2 : 1.1, hd: along ? 1.1 : 2.2 },
    });
  }
  // Bollards at the corners of each crossroads.
  for (const x of lines)
    for (const z of lines)
      for (const [dx, dz] of [[1, 1], [1, -1], [-1, 1], [-1, -1]])
        add({ model: "city/Prop_Bollard", x: x + dx * (HALF - 3.6), z: z + dz * (CITY.ROAD + 0.5), rot: 0, scale: 1, kind: "prop", collide: 0.2 });

  // 4. The plaza: trees in planters round the edge, leaving the middle and the
  // four entrances open.
  const P = PLAZA_BLOCK;
  for (let k = -3; k <= 3; k++) {
    if (Math.abs(k) < 1) continue;
    const t = k * 7.5;
    for (const [x, z] of [[P.x + t, P.z - inner + 2.5], [P.x + t, P.z + inner - 2.5], [P.x - inner + 2.5, P.z + t], [P.x + inner - 2.5, P.z + t]] as [number, number][]) {
      add({ model: "city/Prop_Planter_Single", x, z, rot: 0, scale: 1, kind: "prop", collide: 1.05 });
      add({ model: `nature/CommonTree_${1 + Math.floor(rand() * 5)}`, x, z, rot: r(0, 6.3), scale: r(0.75, 0.9), kind: "tree", collide: 0 });
    }
  }

  // Benches between the planters, facing the middle of the square, with a bin at each end of a side.
  for (const [ox, oz] of [[0, 1], [0, -1], [1, 0], [-1, 0]])
    for (const t of [-18.75, -11.25, 11.25, 18.75]) {
      const d = inner - 2.5;
      const x = P.x + (ox ? ox * d : t), z = P.z + (oz ? oz * d : t);
      add({ model: "kaykit-city/bench.gltf", x, z, rot: Math.atan2(P.x - x, P.z - z), scale: 4.8, kind: "prop", collide: 0.7 });
      if (Math.abs(t) > 18) add({ model: "kaykit-city/trash_A.gltf", x: x + (ox ? 0 : Math.sign(t) * 2.4), z: z + (oz ? 0 : Math.sign(t) * 2.4), rot: 0, scale: 4.5, kind: "prop", collide: 0.35 });
    }

  // 5. The park: trees and bushes around an open lawn in the middle.
  const K = PARK_BLOCK;
  for (let i = 0; i < 70; i++) {
    const x = K.x + r(-inner + 2, inner - 2), z = K.z + r(-inner + 2, inner - 2);
    if (Math.hypot(x - K.x, z - K.z) < 9) continue; // keep the middle clear to stand in
    if (out.some((p) => p.kind === "tree" && Math.hypot(p.x - x, p.z - z) < 5.5)) continue;
    const big = rand() < 0.65;
    add({
      model: big ? `nature/${pick(["CommonTree_1", "CommonTree_2", "CommonTree_3", "CommonTree_4", "CommonTree_5", "Pine_1", "Pine_3"])}` : `nature/${pick(["Bush_Common", "Bush_Common_Flowers"])}`,
      x, z, rot: r(0, 6.3), scale: big ? r(0.9, 1.3) : r(1, 1.5), kind: big ? "tree" : "plant", collide: big ? 0.6 : 0.9,
    });
  }
  // Benches around the lawn, facing the middle, with bins.
  for (const side of [-1, 1])
    for (const t of [-12, 0, 12]) {
      const x = K.x + side * 15, z = K.z + t;
      add({ model: "kaykit-city/bench.gltf", x, z, rot: Math.atan2(K.x - x, 0), scale: 4.8, kind: "prop", collide: 0.7 });
      if (t === 12) add({ model: "kaykit-city/trash_B.gltf", x, z: z + 2.4, rot: 0, scale: 4.5, kind: "prop", collide: 0.35 });
    }
  for (let i = 0; i < 260; i++) {
    const x = K.x + r(-inner, inner), z = K.z + r(-inner, inner);
    if (Math.hypot(x - K.x, z - K.z) < 7) continue;
    add({
      model: `nature/${pick(["Grass_Common_Short", "Grass_Wispy_Short", "Grass_Common_Short", "Flower_3_Group", "Flower_4_Group", "Plant_1", "Fern_1"])}`,
      x, z, rot: r(0, 6.3), scale: r(0.8, 1.2), kind: "plant", collide: 0,
    });
  }
  // Park lap gate: two crates on the open lawn, south of the middle. Fixed
  // spots so the dice above are not rolled again. The gap between them is the start.
  for (const crate of parkCourse(K).crates)
    add({ model: crate.model, x: crate.x, z: crate.z, rot: 0, scale: 4.2, kind: "prop", collide: 0.45 });

  // Extra detail from packs that were already downloaded: a separate seed so the
  // streets, buildings and parked cars above stay put.
  const detail = seeded(20261024);
  const rd = (a = 0, b = 1) => a + detail() * (b - a);
  const pickD = <V>(list: readonly V[]) => list[Math.floor(detail() * list.length)];
  for (const s of lines)
    for (let i = 0; i < lines.length - 1; i++) {
      const mid = (lines[i] + lines[i + 1]) / 2;
      add({ model: "city/Prop_ManholeCover", x: mid, z: s, rot: 0, scale: 1, kind: "prop", collide: 0 });
      add({ model: "city/Prop_ManholeCover", x: s, z: mid, rot: 0, scale: 1, kind: "prop", collide: 0 });
      for (const side of [-1, 1]) {
        if (Math.abs(s) > L && Math.sign(s) === side) continue;
        add({ model: "city/Prop_Drain", x: mid, z: s + side * (CITY.ROAD - 0.35), rot: side > 0 ? 0 : Math.PI, scale: 1, kind: "prop", collide: 0 });
        add({ model: "city/Prop_Drain", x: s + side * (CITY.ROAD - 0.35), z: mid, rot: side > 0 ? Math.PI / 2 : -Math.PI / 2, scale: 1, kind: "prop", collide: 0 });
      }
    }
  // Flowers and rocks around the meeting point. The middle stays empty so a
  // group can stand together, and the east approach (the guided walk) stays clear.
  for (let i = 0; i < 18; i++) {
    const a = (i / 18) * Math.PI * 2;
    const rad = 5.2 + (i % 2) * 1.6;
    add({
      model: `nature/${i % 3 === 0 ? "Fern_1" : i % 3 === 1 ? "Flower_4_Group" : "Flower_3_Group"}`,
      x: PLAZA_BLOCK.x + Math.cos(a) * rad,
      z: PLAZA_BLOCK.z + Math.sin(a) * rad,
      rot: a,
      scale: rd(1, 1.35),
      kind: "plant",
      collide: 0,
    });
  }
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2 + 0.5;
    add({
      model: i % 2 ? "nature/Rock_Medium_1" : "nature/Rock_Medium_2",
      x: PLAZA_BLOCK.x + Math.cos(a) * 8.8,
      z: PLAZA_BLOCK.z + Math.sin(a) * 8.8,
      rot: a,
      scale: rd(0.9, 1.4),
      kind: "prop",
      collide: 0.65,
    });
  }
  for (const [ox, oz] of [[1, 1], [1, -1], [-1, 1], [-1, -1]] as [number, number][]) {
    add({ model: "kaykit-city/box_A.gltf", x: PLAZA_BLOCK.x + ox * (inner - 6), z: PLAZA_BLOCK.z + oz * (inner - 6.4), rot: rd(0, 6), scale: 4.2, kind: "prop", collide: 0.45 });
    add({ model: "kaykit-city/box_B.gltf", x: PLAZA_BLOCK.x + ox * (inner - 7.2), z: PLAZA_BLOCK.z + oz * (inner - 5.2), rot: rd(0, 6), scale: 3.8, kind: "prop", collide: 0.35 });
    add({
      model: "kaykit-city/dumpster.gltf",
      x: PARK_BLOCK.x + ox * (inner - 3.5),
      z: PARK_BLOCK.z + oz * (inner - 3.5),
      rot: Math.atan2(ox, oz),
      scale: 4.6,
      kind: "prop",
      collide: 0.9,
    });
  }
  for (let i = 0; i < 14; i++) {
    const a = (i / 14) * Math.PI * 2;
    const rad = rd(11, inner - 3);
    const model = pickD(["nature/Rock_Medium_1", "nature/Rock_Medium_2", "nature/Fern_1"]);
    const plant = model.endsWith("Fern_1");
    add({
      model,
      x: PARK_BLOCK.x + Math.cos(a) * rad,
      z: PARK_BLOCK.z + Math.sin(a) * rad,
      rot: a,
      scale: rd(0.8, 1.5),
      kind: plant ? "plant" : "prop",
      collide: plant ? 0 : 0.45,
    });
  }
  return out;
}

export const layout = buildLayout();

/** Everything solid in the city (for the game and the multiplayer server alike).
 * Parked cars' boxes are the layout objects themselves, so moving a car moves its obstacle. */
export function staticObstacles(): Obstacle[] {
  const out: Obstacle[] = [];
  for (const p of layout) {
    if (p.box) out.push(p.box);
    else if (p.collide > 0) out.push({ x: p.x, z: p.z, r: p.collide });
  }
  return out;
}
